import {css} from '@emotion/react';
import {Box, Divider, Group, Image, Paper, Stack, Text} from '@mantine/core';
import {FC} from 'react';
import {useAsync} from 'react-use';
import {JobsService} from '../api/generated/JobsService';
import logo from '../assets/mm_logo.png';
import {AsyncStateContainer} from '../components/async-state-container';
import {useNotification} from '../hooks/use-notifications';
import {DefaultThemeColors} from '../styles/default-theme';

export const Dashboard: FC = () => {
  const notification = useNotification();
  const {value, loading} = useAsync(async () => {
    const {data, hasErrors} = await JobsService.getDashboardInfo();
    if (hasErrors)
      notification.error('There was an issue retrieving the dashboard info.');
    return data;
  }, [notification]);

  return (
    <AsyncStateContainer loading={loading}>
      {value && (
        <Box p='xl'>
          <Image
            alt='MMValve ARF logo'
            fit='contain'
            src={logo}
            m='0 auto'
            mt='sm'
            h={200}
          />
          <Text ta='center' m='xl' fz={22} fw={600}>
            M&M International ARF Services
          </Text>
          <Stack w='50%' ml='auto' mr='auto' gap={8}>
            <Group w='40%' m='4px' ml='calc(60% - 8px)' grow gap={8}>
              <Text ta='left' fz={18} fw={600}>
                Jobs
              </Text>
              <Text ta='center' fz={18} fw={600}>
                Median Days
              </Text>
            </Group>
            <Row
              title={`${value.evaluation?.title}`}
              bg={DefaultThemeColors.primary['7']}
              jobs={Number(value.evaluation?.jobCount)}
              days={Number(value.evaluation?.dayCount)}
            />
            <Row
              title={`${value.archiving?.title}`}
              bg={DefaultThemeColors.primaryAccent['9']}
              jobs={Number(value.archiving?.jobCount)}
              days={Number(value.archiving?.dayCount)}
            />
            <Row
              title={`${value.closedArchived?.title}`}
              bg={DefaultThemeColors.secondaryAccent['9']}
              jobs={Number(value.closedArchived?.jobCount)}
              days={Number(value.closedArchived?.dayCount)}
            />
          </Stack>
          <Divider mt='xl' />
          <Stack c='rgba(0,0,0,0.75)' gap={8} m='lg'>
            <Text c='black' fz={18} ta='center' fw={600}>
              Contact Us
            </Text>
            <Text ta='center'>{value.customerSupportHours}</Text>
            <Text ta='center'>{value.customerSupportEmail}</Text>
            <Text ta='center'>{value.customerSupportPhone}</Text>
          </Stack>
        </Box>
      )}
    </AsyncStateContainer>
  );
};

type RowProps = {
  title: string;
  bg: string;
  jobs: number;
  days: number;
};

const Row: FC<RowProps> = ({title, bg, jobs, days}) => (
  <Group gap={0}>
    <Group gap={8} w='60%' wrap='nowrap'>
      <Paper withBorder={false} bg={bg} radius={12} p='0 10px' shadow='none'>
        <Text m='4px' ta='center' fz={18} fw={600} c='white'>
          {title}
        </Text>
      </Paper>
      <Box css={boxCss} flex={1} mr={8} />
    </Group>
    <Group w='40%' grow ml='auto' gap={8}>
      <Text m='4px' ta='left' ml={4} fz={18} fw={400}>
        {jobs}
      </Text>
      <Text m='4px' ta='center' fz={18} fw={400}>
        {days}
      </Text>
    </Group>
  </Group>
);

const boxCss = css({
  borderBottom: '1px dotted rgba(0, 0, 0, 0.1)',
});
